/**
 * FTS Simulation Animation Page
 * Visualizes aircraft and GSE movement using UnifiedAirportMap.
 * Aircraft rendered by AircraftDeckLayer (Deck.gl IconLayer, real wingspan scale).
 * Trails: PathLayer (last 45 frames per aircraft, status-colored).
 * GSE, stand labels, and AI speech bubbles managed by SimPlugin via useMapEngine().
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import { useLanguage } from '../context/LanguageContext';

import UnifiedAirportMap from '../components/map-engine/UnifiedAirportMap';
import { useMapEngine } from '../components/map-engine/MapEngineContext';
import { getIcaoCategory } from '../components/map-engine/aircraft/aircraftTypes';
import type { AircraftState, AircraftStatus } from '../types/mapEngine';

import AnimationConfigModal, { AnimationConfig } from '../components/fts/AnimationConfigModal';
import { API_URL } from '../services/api';

import {
  ClockIcon,
  PlayIcon,
  PauseIcon,
  ForwardIcon,
  BackwardIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AIIntervention {
  action: string;
  target?: string;
  reason?: string;
}

interface AircraftFrame {
  timestamp: number;
  flight_id: string;
  aircraft_type: string;
  position: [number, number];
  heading: number;
  phase: string;
  is_arrival: boolean;
  ai_intervention?: AIIntervention;
}

interface GSEFrame {
  timestamp: number;
  vehicle_type: string;
  position: [number, number];
  status: string;
}

interface StandMarker {
  id: string;
  position: [number, number];
  name: string;
}

// ─── i18n ─────────────────────────────────────────────────────────────────────

const TEXTS = {
  es: {
    goBack: 'Volver', title: 'Animación FTS', time: 'Tiempo',
    playbackSpeed: 'Velocidad de Reproducción', hourEquals: '1 hora =',
    sec: 'seg', minSim: '1 min sim =', secReal: 'seg real',
    aircraft: 'Aeronaves', config: 'Configuración', legend: 'Leyenda',
    arrival: 'Llegada', departure: 'Salida',
    missingToken: 'Token de Mapbox Ausente',
    configureToken: 'Configurar VITE_MAPBOX_TOKEN en .env',
    loadingMap: 'Cargando simulación...',
    arrivals: 'Llegadas', departures: 'Salidas',
    activeAircraft: 'Aeronaves activas',
    status: 'Estado', heading: 'Hdg',
    hudClose: 'Cerrar',
  },
  en: {
    goBack: 'Go Back', title: 'FTS Animation', time: 'Time',
    playbackSpeed: 'Playback Speed', hourEquals: '1 hour =',
    sec: 'sec', minSim: '1 min sim =', secReal: 'sec real',
    aircraft: 'Aircraft', config: 'Configuration', legend: 'Legend',
    arrival: 'Arrival', departure: 'Departure',
    missingToken: 'Missing Mapbox Token',
    configureToken: 'Configure VITE_MAPBOX_TOKEN in .env',
    loadingMap: 'Loading simulation...',
    arrivals: 'Arrivals', departures: 'Departures',
    activeAircraft: 'Active aircraft',
    status: 'Status', heading: 'Hdg',
    hudClose: 'Close',
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function phaseToStatus(phase: string): AircraftStatus {
  const p = (phase || '').toLowerCase();
  if (p.includes('park'))    return 'parked';
  if (p.includes('push'))    return 'pushback';
  if (p.includes('hold'))    return 'holding';
  if (p.includes('emerg'))   return 'emergency';
  return 'taxiing';
}

function frameToAircraftState(frame: AircraftFrame): AircraftState {
  return {
    id:           frame.flight_id,
    callsign:     frame.flight_id,
    icaoType:     frame.aircraft_type || 'B738',
    icaoCategory: getIcaoCategory(frame.aircraft_type || 'B738'),
    position:     frame.position,
    heading:      frame.heading || 0,
    status:       phaseToStatus(frame.phase),
  };
}

const STATUS_COLOR: Record<string, string> = {
  parked:    '#a0a0a0',
  pushback:  '#ff9800',
  taxiing:   '#ffd700',
  holding:   '#26c6da',
  emergency: '#f44336',
};

const BUBBLE_DURATION_MS = 3000;

// ─── SimPlugin ────────────────────────────────────────────────────────────────

interface SimPluginProps {
  gse: GSEFrame[];
  stands: StandMarker[];
  speechBubbles: Map<string, { intervention: AIIntervention; expiresAt: number }>;
  showGSE: boolean;
}

function SimPlugin({ gse, stands, speechBubbles, showGSE }: SimPluginProps) {
  const { map, mapLoaded, aircraft } = useMapEngine();
  const gseMarkersRef    = useRef<mapboxgl.Marker[]>([]);
  const bubbleMarkersRef = useRef<Map<string, mapboxgl.Marker>>(new Map());
  const standsAddedRef   = useRef(false);

  // Stand labels — static, added once
  useEffect(() => {
    if (!map || !mapLoaded || standsAddedRef.current || stands.length === 0) return;
    standsAddedRef.current = true;
    stands.forEach((s) => {
      const el = document.createElement('div');
      const w = Math.max(24, s.name.length * 6 + 8);
      el.style.cssText = `min-width:${w}px;height:20px;padding:0 4px;background:#3A7CA5;border:2px solid white;border-radius:4px;display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:bold;color:white;box-shadow:0 2px 4px rgba(0,0,0,0.3);white-space:nowrap;`;
      el.textContent = s.name;
      new mapboxgl.Marker({ element: el }).setLngLat(s.position).addTo(map);
    });
  }, [map, mapLoaded, stands]);

  // GSE markers — update each animation tick
  useEffect(() => {
    if (!map || !mapLoaded) return;
    gseMarkersRef.current.forEach((m) => m.remove());
    gseMarkersRef.current = [];
    if (!showGSE) return;
    gse.forEach((g) => {
      const el = document.createElement('div');
      el.style.cssText = 'width:12px;height:12px;border-radius:50%;background:#ffd700;border:1px solid #333;';
      const m = new mapboxgl.Marker({ element: el }).setLngLat(g.position).addTo(map);
      gseMarkersRef.current.push(m);
    });
    return () => { gseMarkersRef.current.forEach((m) => m.remove()); };
  }, [gse, map, mapLoaded, showGSE]);

  // AI speech bubble markers
  useEffect(() => {
    if (!map || !mapLoaded) return;
    const now = Date.now();

    for (const [id, marker] of bubbleMarkersRef.current) {
      const b = speechBubbles.get(id);
      if (!b || b.expiresAt <= now) {
        marker.remove();
        bubbleMarkersRef.current.delete(id);
      }
    }

    for (const [id, bubble] of speechBubbles) {
      if (bubble.expiresAt <= now) continue;
      if (bubbleMarkersRef.current.has(id)) continue;
      const ac = aircraft.find((a) => a.id === id);
      if (!ac) continue;
      const el = document.createElement('div');
      el.style.cssText = 'background:#1a2a3a;border:1px solid #F26430;border-radius:6px;padding:4px 8px;white-space:nowrap;font-size:11px;font-family:monospace;color:#F26430;pointer-events:none;box-shadow:0 2px 8px rgba(0,0,0,0.4);';
      el.textContent = `IA: ${bubble.intervention.action}${bubble.intervention.target ? ' — ' + bubble.intervention.target : ''}`;
      const marker = new mapboxgl.Marker({ element: el, offset: [0, -32] })
        .setLngLat(ac.position)
        .addTo(map);
      bubbleMarkersRef.current.set(id, marker);
    }
  }, [speechBubbles, aircraft, map, mapLoaded]);

  return null;
}

// ─── Aircraft HUD (floating panel over map) ───────────────────────────────────

interface AircraftHUDProps {
  aircraft: AircraftState;
  isArrival: boolean;
  onClose: () => void;
}

function AircraftHUD({ aircraft: ac, isArrival, onClose }: AircraftHUDProps) {
  const statusColor = STATUS_COLOR[ac.status] ?? '#a0a0a0';
  return (
    <div className="absolute top-4 right-4 z-30 w-56 rounded-xl border border-[#2a2a30] bg-[#13131a]/95 backdrop-blur-sm p-4 shadow-2xl">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-mono text-base font-bold text-white">{ac.callsign}</div>
          <div className="text-xs text-[var(--text-secondary)]">{ac.icaoType} · Cat {ac.icaoCategory}</div>
        </div>
        <button
          onClick={onClose}
          className="p-0.5 text-[var(--text-secondary)] hover:text-white"
          aria-label="Close HUD"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-2 text-xs">
        <div className="flex justify-between">
          <span className="text-[var(--text-secondary)]">Estado</span>
          <span className="font-medium" style={{ color: statusColor }}>
            {ac.status.charAt(0).toUpperCase() + ac.status.slice(1)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-[var(--text-secondary)]">Tipo</span>
          <span className="font-medium text-white">{isArrival ? 'Llegada' : 'Salida'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[var(--text-secondary)]">Hdg</span>
          <span className="font-mono text-white">{Math.round(ac.heading)}°</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[var(--text-secondary)]">Pos</span>
          <span className="font-mono text-white text-[10px]">
            {ac.position[1].toFixed(4)}, {ac.position[0].toFixed(4)}
          </span>
        </div>
      </div>

      {/* Status bar */}
      <div
        className="mt-3 h-1 w-full rounded-full"
        style={{ backgroundColor: statusColor }}
      />
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const SimulationAnimationPage: React.FC = () => {
  const { language } = useLanguage();
  const tx = TEXTS[(language as 'es' | 'en') || 'en'] || TEXTS.en;
  const { id: simulationId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const mapRef    = useRef<mapboxgl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  // Data
  const [icaoCode,       setIcaoCode]       = useState('');
  const [airportCenter,  setAirportCenter]  = useState<[number, number]>([0, 0]);
  const [aircraftFrames, setAircraftFrames] = useState<AircraftFrame[]>([]);
  const [gseFrames,      setGseFrames]      = useState<GSEFrame[]>([]);
  const [stands,         setStands]         = useState<StandMarker[]>([]);
  const [startTime,  setStartTime]  = useState(360);
  const [endTime,    setEndTime]    = useState(1080);
  const [isLoading,  setIsLoading]  = useState(true);

  // Playback
  const [currentTime,     setCurrentTime]     = useState(360);
  const [isPlaying,       setIsPlaying]       = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [config, setConfig] = useState<AnimationConfig>({
    frameRate: 1, playbackSpeed: 60, showGSE: true,
    showTrails: true, trailLength: 120, daySelection: 'busiest',
  });

  // UI state
  const [showAircraftList, setShowAircraftList] = useState(false);
  const [selectedAircraft, setSelectedAircraft] = useState<AircraftState | null>(null);

  // AI speech bubbles
  const [speechBubbles, setSpeechBubbles] = useState<
    Map<string, { intervention: AIIntervention; expiresAt: number }>
  >(new Map());

  // ─── Load simulation data ──────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      if (!simulationId) return;
      try {
        const simResp = await fetch(`${API_URL}/simulations/${simulationId}`);
        if (simResp.ok) {
          const simData = await simResp.json();
          if (simData.project) {
            setIcaoCode(
              simData.project.icao_code || simData.project.iata_code || 'MPTO',
            );
            const analysis = simData.project.analysis_results;
            if (analysis?.center) {
              setAirportCenter([analysis.center.lon, analysis.center.lat]);
            }
            const geojson = simData.project.geojson_data;
            if (geojson?.features?.[0]?.geometry?.coordinates) {
              const c = geojson.features[0].geometry.coordinates;
              if (Array.isArray(c) && c.length >= 2) setAirportCenter([c[0], c[1]]);
            }
          }
        }

        const animResp = await fetch(`${API_URL}/simulations/${simulationId}/animation`);
        if (animResp.ok) {
          const data = await animResp.json();
          if (data.aircraft)  setAircraftFrames(data.aircraft);
          if (data.gse)       setGseFrames(data.gse);
          if (data.stands)    setStands(data.stands);
          if (data.metadata) {
            setStartTime(data.metadata.start_time  || 360);
            setEndTime(  data.metadata.end_time    || 1080);
            setCurrentTime(data.metadata.start_time || 360);
            if (data.metadata.center) {
              setAirportCenter([data.metadata.center.lon, data.metadata.center.lat]);
            }
          }
        }
      } catch (err) {
        console.error('[SimulationPage] load failed:', err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [simulationId]);

  // ─── Animation loop (30 FPS) ───────────────────────────────────────────────

  useEffect(() => {
    if (!isPlaying) return;
    const frameIntervalMs = 1000 / 30;
    const timeInc = (config.playbackSpeed / 60) * (frameIntervalMs / 1000);
    const id = setInterval(() => {
      setCurrentTime((prev) => {
        const next = prev + timeInc;
        if (next >= endTime) { setIsPlaying(false); return startTime; }
        return next;
      });
    }, frameIntervalMs);
    return () => clearInterval(id);
  }, [isPlaying, config.playbackSpeed, startTime, endTime]);

  // ─── Position interpolation ────────────────────────────────────────────────

  const getCurrentAircraft = useCallback((): AircraftFrame[] => {
    const byId = new Map<string, AircraftFrame[]>();
    aircraftFrames.forEach((f) => {
      if (!byId.has(f.flight_id)) byId.set(f.flight_id, []);
      byId.get(f.flight_id)!.push(f);
    });

    const result: AircraftFrame[] = [];
    byId.forEach((frames) => {
      frames.sort((a, b) => a.timestamp - b.timestamp);
      let prev: AircraftFrame | null = null;
      let next: AircraftFrame | null = null;
      for (let i = 0; i < frames.length; i++) {
        if (frames[i].timestamp <= currentTime) {
          prev = frames[i]; next = frames[i + 1] || null;
        } else break;
      }
      if (!prev) return;
      if (currentTime > frames[frames.length - 1].timestamp + 2) return;

      if (next && next.timestamp > prev.timestamp) {
        const t = (currentTime - prev.timestamp) / (next.timestamp - prev.timestamp);
        const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        const lat = prev.position[1] + (next.position[1] - prev.position[1]) * e;
        const lon = prev.position[0] + (next.position[0] - prev.position[0]) * e;
        let hdiff = next.heading - prev.heading;
        if (hdiff > 180) hdiff -= 360; if (hdiff < -180) hdiff += 360;
        result.push({ ...prev, position: [lon, lat], heading: prev.heading + hdiff * e,
          phase: e < 0.5 ? prev.phase : (next.phase || prev.phase) });
      } else {
        result.push(prev);
      }
    });
    return result;
  }, [aircraftFrames, currentTime]);

  const getCurrentGSE = useCallback(() =>
    config.showGSE
      ? gseFrames.filter((g) => Math.abs(g.timestamp - currentTime) < 1)
      : [],
    [gseFrames, currentTime, config.showGSE]);

  // ─── Speech bubble management ──────────────────────────────────────────────

  const currentAircraft = getCurrentAircraft();

  useEffect(() => {
    const now = Date.now();
    currentAircraft.forEach((a) => {
      if (!a.ai_intervention) return;
      setSpeechBubbles((prev) => {
        const existing = prev.get(a.flight_id);
        if (existing && existing.intervention.action === a.ai_intervention!.action) return prev;
        const next = new Map(prev);
        next.set(a.flight_id, { intervention: a.ai_intervention!, expiresAt: now + BUBBLE_DURATION_MS });
        return next;
      });
    });
  }, [currentTime]);

  useEffect(() => {
    if (speechBubbles.size === 0) return;
    const nextExpiry = Math.min(...Array.from(speechBubbles.values()).map((b) => b.expiresAt));
    const delay = nextExpiry - Date.now();
    const id = setTimeout(() => {
      setSpeechBubbles((prev) => {
        const t = Date.now();
        const next = new Map(prev);
        for (const [id, b] of next) { if (b.expiresAt <= t) next.delete(id); }
        return next;
      });
    }, Math.max(0, delay));
    return () => clearTimeout(id);
  }, [speechBubbles]);

  // ─── Keep selected aircraft in sync (update its state each frame) ──────────

  useEffect(() => {
    if (!selectedAircraft) return;
    const updated = aircraftStates.find((a) => a.id === selectedAircraft.id);
    if (!updated) { setSelectedAircraft(null); return; }
    // Only update if position or status changed to avoid churn
    if (
      updated.position[0] !== selectedAircraft.position[0] ||
      updated.position[1] !== selectedAircraft.position[1] ||
      updated.status !== selectedAircraft.status ||
      updated.heading !== selectedAircraft.heading
    ) {
      setSelectedAircraft(updated);
    }
  }, [currentTime]);

  // ─── Controls ─────────────────────────────────────────────────────────────

  const formatTime = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = Math.floor(mins % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  const handlePlay  = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);
  const handleReset = () => { setIsPlaying(false); setCurrentTime(startTime); };
  const handleSkipForward = () => setCurrentTime((p) => Math.min(p + 60, endTime));
  const handleSkipBack    = () => setCurrentTime((p) => Math.max(p - 60, startTime));

  const aircraftStates: AircraftState[] = currentAircraft.map(frameToAircraftState);

  // Arrivals / departures breakdown
  const arrivalsCount   = currentAircraft.filter((f) => f.is_arrival).length;
  const departuresCount = currentAircraft.filter((f) => !f.is_arrival).length;

  const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || '';

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="flex h-[calc(100vh-64px)] bg-[var(--background)] text-[var(--text-primary)]">
        {/* Sidebar */}
        <aside className="w-80 overflow-y-auto border-r border-[var(--border)] bg-[var(--surface)] p-4 shrink-0">
          <div className="mb-4">
            <button
              onClick={() => navigate(-1)}
              className="mb-2 inline-flex items-center text-sm text-[var(--text-secondary)] hover:text-[#F26430]"
            >
              <ChevronLeftIcon className="mr-1 h-4 w-4" />
              {tx.goBack}
            </button>
            <h2 className="text-lg font-semibold">{tx.title}</h2>
          </div>

          {/* Time */}
          <div className="mb-6 rounded-lg bg-[var(--surface-raised)] p-4">
            <div className="mb-2 flex items-center gap-2 text-[var(--text-secondary)]">
              <ClockIcon className="h-4 w-4" />
              <span className="text-sm">{tx.time}</span>
            </div>
            <div className="text-3xl font-bold">{formatTime(currentTime)}</div>
            <div className="mt-1 text-sm text-[var(--text-tertiary)]">
              {config.playbackSpeed === 60  ? 'x1'  :
               config.playbackSpeed === 120 ? 'x2'  :
               config.playbackSpeed === 300 ? 'x5'  :
               config.playbackSpeed === 600 ? 'x10' :
               `${config.playbackSpeed}x`}
            </div>
          </div>

          {/* Timeline */}
          <div className="mb-6">
            <input type="range" min={startTime} max={endTime} value={currentTime}
              onChange={(e) => setCurrentTime(Number(e.target.value))}
              className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-[var(--surface-inset)] accent-[#F26430]"
            />
            <div className="mt-1 flex justify-between text-xs text-[var(--text-tertiary)]">
              <span>{formatTime(startTime)}</span>
              <span>{formatTime(endTime)}</span>
            </div>
          </div>

          {/* Playback controls */}
          <div className="mb-6 flex justify-center gap-2">
            <button onClick={handleSkipBack} className="rounded-lg bg-[var(--surface-raised)] p-3 hover:bg-[var(--surface-inset)]">
              <BackwardIcon className="h-5 w-5" />
            </button>
            {isPlaying ? (
              <button onClick={handlePause} className="rounded-lg bg-[#F26430] p-3 text-white hover:bg-[#d14f1f]">
                <PauseIcon className="h-5 w-5" />
              </button>
            ) : (
              <button onClick={handlePlay} className="rounded-lg bg-[#F26430] p-3 text-white hover:bg-[#d14f1f]">
                <PlayIcon className="h-5 w-5" />
              </button>
            )}
            <button onClick={handleSkipForward} className="rounded-lg bg-[var(--surface-raised)] p-3 hover:bg-[var(--surface-inset)]">
              <ForwardIcon className="h-5 w-5" />
            </button>
            <button onClick={handleReset} className="rounded-lg bg-[var(--surface-raised)] p-3 hover:bg-[var(--surface-inset)]">
              <ArrowPathIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Speed selector */}
          <div className="mb-6">
            <div className="text-xs text-[var(--text-secondary)] mb-2 text-center">{tx.playbackSpeed}</div>
            <div className="flex justify-center gap-1">
              {([{ label: 'x1', speed: 60 }, { label: 'x2', speed: 120 },
                 { label: 'x5', speed: 300 }, { label: 'x10', speed: 600 }] as const
               ).map(({ label, speed }) => (
                <button key={speed}
                  onClick={() => setConfig((c) => ({ ...c, playbackSpeed: speed }))}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                    config.playbackSpeed === speed
                      ? 'bg-[#F26430] text-white'
                      : 'bg-[var(--surface-raised)] text-[var(--text-secondary)] hover:bg-[var(--surface-inset)]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="text-xs text-[var(--text-tertiary)] mt-2 text-center">
              {`${tx.hourEquals} ${Math.round(3600 / config.playbackSpeed)} ${tx.sec}`}
            </div>
          </div>

          {/* Stats — 4-cell grid with arrivals/departures split */}
          <div className="mb-6 grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-[var(--surface-raised)] p-3">
              <div className="text-2xl font-bold text-[#F26430]">{arrivalsCount}</div>
              <div className="text-xs text-[var(--text-secondary)]">{tx.arrivals}</div>
            </div>
            <div className="rounded-lg bg-[var(--surface-raised)] p-3">
              <div className="text-2xl font-bold text-emerald-400">{departuresCount}</div>
              <div className="text-xs text-[var(--text-secondary)]">{tx.departures}</div>
            </div>
            <div className="rounded-lg bg-[var(--surface-raised)] p-3">
              <div className="text-2xl font-bold text-white">{aircraftStates.length}</div>
              <div className="text-xs text-[var(--text-secondary)]">{tx.aircraft}</div>
            </div>
            <div className="rounded-lg bg-[var(--surface-raised)] p-3">
              <div className="text-2xl font-bold text-yellow-400">{getCurrentGSE().length}</div>
              <div className="text-xs text-[var(--text-secondary)]">GSE</div>
            </div>
          </div>

          {/* Active aircraft list (collapsible) */}
          <div className="mb-6">
            <button
              onClick={() => setShowAircraftList((v) => !v)}
              className="flex w-full items-center justify-between text-xs font-medium text-[var(--text-secondary)] mb-2 hover:text-white"
            >
              <span>{tx.activeAircraft} ({aircraftStates.length})</span>
              {showAircraftList
                ? <ChevronUpIcon className="h-3.5 w-3.5" />
                : <ChevronDownIcon className="h-3.5 w-3.5" />
              }
            </button>
            {showAircraftList && (
              <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
                {aircraftStates.length === 0 ? (
                  <div className="text-xs text-[var(--text-tertiary)] text-center py-2">—</div>
                ) : (
                  aircraftStates.map((ac) => {
                    const frame = currentAircraft.find((f) => f.flight_id === ac.id);
                    const isArr = frame?.is_arrival ?? false;
                    const isSelected = selectedAircraft?.id === ac.id;
                    return (
                      <button
                        key={ac.id}
                        onClick={() => setSelectedAircraft(isSelected ? null : ac)}
                        className={`w-full flex items-center justify-between rounded-md px-2 py-1.5 text-left text-xs transition-colors ${
                          isSelected
                            ? 'bg-[#F26430]/20 border border-[#F26430]/40'
                            : 'bg-[var(--surface-raised)] hover:bg-[var(--surface-inset)]'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="h-2 w-2 rounded-full shrink-0"
                            style={{ backgroundColor: STATUS_COLOR[ac.status] ?? '#a0a0a0' }}
                          />
                          <span className="font-mono font-medium text-white">{ac.callsign}</span>
                          <span className="text-[var(--text-tertiary)]">{ac.icaoType}</span>
                        </div>
                        <div className="flex items-center gap-1 text-[var(--text-tertiary)]">
                          <span>{isArr ? '→' : '←'}</span>
                          <span>{Math.round(ac.heading)}°</span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Config */}
          <button
            onClick={() => setShowConfigModal(true)}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--border)] py-2 text-[var(--text-secondary)] hover:border-[#F26430] hover:text-[#F26430]"
          >
            <Cog6ToothIcon className="h-4 w-4" />
            {tx.config}
          </button>

          {/* Legend */}
          <div className="mt-6 space-y-2">
            <h3 className="text-sm font-medium text-[var(--text-secondary)]">{tx.legend}</h3>
            {(Object.entries(STATUS_COLOR) as [string, string][]).map(([status, color]) => (
              <div key={status} className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-xs text-[var(--text-secondary)] capitalize">{status}</span>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <div className="h-4 w-6 rounded bg-[#3A7CA5] flex items-center justify-center text-[8px] text-white font-bold">A1</div>
              <span className="text-xs text-[var(--text-secondary)]">Stand</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-yellow-400" />
              <span className="text-xs text-[var(--text-secondary)]">GSE</span>
            </div>
          </div>
        </aside>

        {/* Map area */}
        <main className="relative flex-1 overflow-hidden">
          {!MAPBOX_TOKEN ? (
            <div className="flex h-full w-full items-center justify-center bg-[var(--surface)]">
              <div className="text-center max-w-md p-8">
                <ExclamationTriangleIcon className="w-10 h-10 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">{tx.missingToken}</h3>
                <p className="text-[var(--text-secondary)]">{tx.configureToken}</p>
              </div>
            </div>
          ) : icaoCode ? (
            <UnifiedAirportMap
              mode="fts-simulation"
              icaoCode={icaoCode}
              initialCenter={airportCenter[0] !== 0 ? airportCenter : undefined}
              aircraft={aircraftStates}
              showTrails={config.showTrails}
              onAircraftClick={(ac) => setSelectedAircraft(ac)}
              onMapReady={(m) => { mapRef.current = m; setMapReady(true); }}
              className="absolute inset-0"
              style={{ height: '100%' }}
            >
              <SimPlugin
                gse={getCurrentGSE()}
                stands={stands}
                speechBubbles={speechBubbles}
                showGSE={config.showGSE}
              />
            </UnifiedAirportMap>
          ) : null}

          {/* Aircraft HUD overlay */}
          {selectedAircraft && (
            <AircraftHUD
              aircraft={selectedAircraft}
              isArrival={
                currentAircraft.find((f) => f.flight_id === selectedAircraft.id)?.is_arrival ?? false
              }
              onClose={() => setSelectedAircraft(null)}
            />
          )}

          {/* Loading overlay */}
          {(isLoading || (!mapReady && !!icaoCode)) && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 z-20">
              <div className="text-center">
                <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-[#F26430] border-t-transparent mx-auto" />
                <p className="text-[var(--text-secondary)]">{tx.loadingMap}</p>
              </div>
            </div>
          )}
        </main>
      </div>

      <AnimationConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        onApply={setConfig}
        initialConfig={config}
      />
    </>
  );
};

export default SimulationAnimationPage;
